export type DatabaseStatus = {
  configured: boolean;
  reachable: boolean;
  error?: string | null;
};

export type HealthResponse = {
  status: "ok" | "degraded";
  app: string;
  version: string;
  environment: string;
  database: DatabaseStatus;
};

export type VersionResponse = {
  app: string;
  version: string;
  environment: string;
  git_sha: string;
};

export type MeResponse = {
  id: string;
  email: string;
  roles: string[];
  scopes: string[];
};

export type LoginResponse = {
  access_token_exp: number;
  csrf_token: string;
};

export type CsrfResponse = { csrf_token: string };

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";
const CSRF_COOKIE = "irondash_csrf";
const CSRF_HEADER = "X-CSRF-Token";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string | undefined;
  constructor(status: number, detail: string | undefined, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.detail = detail;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=", 2)[1]) : null;
}

// Single-flight refresh: when several requests hit an expired access token at
// once, only one POST /auth/refresh goes out; the rest await the same promise.
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const csrf = readCookie(CSRF_COOKIE);
      const resp = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: csrf ? { [CSRF_HEADER]: csrf } : {},
      });
      return resp.ok;
    } catch {
      return false;
    } finally {
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

// Never refresh-retry these: login/refresh 401s are terminal, and a logout 401
// just means the session is already gone.
const NO_REFRESH_PATHS = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/logout-all"];

export async function request<T>(
  path: string,
  init: RequestInit & { method?: string } = {},
): Promise<T> {
  const method = init.method ?? "GET";

  const doFetch = () => {
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...(init.headers as Record<string, string> | undefined),
    };
    if (init.body && typeof init.body === "string") {
      headers["Content-Type"] ??= "application/json";
    }
    if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
      const csrf = readCookie(CSRF_COOKIE);
      if (csrf) headers[CSRF_HEADER] = csrf;
    }
    return fetch(`${API_BASE}${path}`, {
      ...init,
      method,
      headers,
      credentials: "include",
    });
  };

  let resp = await doFetch();
  if (resp.status === 401 && !NO_REFRESH_PATHS.includes(path)) {
    // Access token expired mid-session (or on boot). The refresh cookie may
    // still be valid — use it, then retry the original request once.
    if (await tryRefreshSession()) resp = await doFetch();
  }
  if (resp.status === 204) return undefined as unknown as T;
  const text = await resp.text();
  const body = text ? (JSON.parse(text) as unknown) : undefined;
  if (!resp.ok) {
    const detail =
      body && typeof body === "object" && "detail" in (body as object)
        ? String((body as { detail: unknown }).detail)
        : undefined;
    throw new ApiError(resp.status, detail);
  }
  return body as T;
}

// ---- Phase 4: master data --------------------------------------------------

export type Company = {
  id: string;
  natural_key: string | null;
  name: string;
  status: string;
  country_id: string | null;
  default_currency: string | null;
};

export type Branch = {
  id: string;
  natural_key: string | null;
  name: string;
  status: string;
  company_id: string;
  brand_id: string | null;
  location_id: string | null;
  timezone: string;
  default_currency: string | null;
};

export type Location = {
  id: string;
  natural_key: string | null;
  name: string;
  status: string;
  city: string | null;
  region: string | null;
  country_id: string | null;
};

export type Employee = {
  id: string;
  natural_key: string | null;
  name: string;
  status: string;
  primary_email: string | null;
  role_hint: string | null;
  company_id: string | null;
  branch_id: string | null;
  department_id: string | null;
};

export type Source = {
  id: string;
  code: string;
  name: string;
  status: string;
  instance_label: string | null;
  is_enabled: boolean;
};

export type Mapping = {
  id: string;
  source_system: string;
  source_entity_type: string;
  source_entity_id: string;
  source_display_name: string | null;
  mapped_dimension: string | null;
  mapped_id: string | null;
  status: "unmapped" | "mapped" | "ignored" | string;
  notes: string | null;
};

export const api = {
  health: () => request<HealthResponse>("/health"),
  version: () => request<VersionResponse>("/version"),

  // Auth
  login: (email: string, password: string) =>
    request<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  me: () => request<MeResponse>("/auth/me"),
  refresh: () => request<LoginResponse>("/auth/refresh", { method: "POST" }),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  logoutAll: () =>
    request<{ revoked: number }>("/auth/logout-all", { method: "POST" }),
  getCsrf: () => request<CsrfResponse>("/auth/csrf"),

  // Master data
  listCompanies: () => request<Company[]>("/admin/masterdata/companies"),
  createCompany: (input: {
    name: string;
    natural_key?: string | null;
    default_currency?: string | null;
  }) =>
    request<Company>("/admin/masterdata/companies", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  listBranches: () => request<Branch[]>("/admin/masterdata/branches"),
  createBranch: (input: {
    name: string;
    company_id: string;
    natural_key?: string | null;
    location_id?: string | null;
    timezone?: string;
    default_currency?: string | null;
  }) =>
    request<Branch>("/admin/masterdata/branches", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  listLocations: () => request<Location[]>("/admin/masterdata/locations"),
  createLocation: (input: {
    name: string;
    natural_key?: string | null;
    city?: string | null;
    region?: string | null;
    country_id?: string | null;
  }) =>
    request<Location>("/admin/masterdata/locations", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  listEmployees: () => request<Employee[]>("/admin/masterdata/employees"),
  createEmployee: (input: {
    name: string;
    natural_key?: string | null;
    primary_email?: string | null;
    role_hint?: string | null;
    company_id?: string | null;
    branch_id?: string | null;
    department_id?: string | null;
  }) =>
    request<Employee>("/admin/masterdata/employees", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  listSources: () => request<Source[]>("/admin/masterdata/sources"),

  // Mappings
  listMappings: (params: {
    source_system?: string;
    source_entity_type?: string;
    status?: string;
  } = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v) q.append(k, v);
    });
    return request<Mapping[]>(
      `/admin/mappings${q.toString() ? `?${q.toString()}` : ""}`,
    );
  },
  listUnmapped: (params: {
    source_system?: string;
    source_entity_type?: string;
  } = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v) q.append(k, v);
    });
    return request<Mapping[]>(
      `/admin/mappings/unmapped${q.toString() ? `?${q.toString()}` : ""}`,
    );
  },
  createMapping: (input: {
    source_system: string;
    source_entity_type: string;
    source_entity_id: string;
    source_display_name?: string | null;
    mapped_dimension?: string | null;
    mapped_id?: string | null;
    notes?: string | null;
  }) =>
    request<Mapping>("/admin/mappings", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  resolveMapping: (
    id: string,
    input: { mapped_dimension: string; mapped_id: string; notes?: string | null },
  ) =>
    request<Mapping>(`/admin/mappings/${id}/resolve`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  setMappingStatus: (
    id: string,
    input: { status: "unmapped" | "mapped" | "ignored"; notes?: string | null },
  ) =>
    request<Mapping>(`/admin/mappings/${id}/status`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
};
